/*
 * Ouverture d'une session esptool-js robuste, partagée par les trois écrans qui
 * flashent par USB.
 *
 * Symptôme d'origine : sur un adaptateur CP2102 externe (Silicon Labs, VID 10C4
 * / PID EA60), tout flash USB s'arrêtait au même endroit, juste après la montée
 * en débit —
 *
 *   Changing baudrate to 460800
 *   Changed
 *   ❌ Unable to verify flash chip connection
 *      Error: Invalid head of packet (0x78)
 *
 * alors que les DevKit à CH340 soudé passaient sans broncher.
 *
 * Ce qui a été écarté, mesures à l'appui, sur ce dongle et cette carte :
 *
 *  - le câblage et la vitesse : esptool.py fait le même 460800 sur le même
 *    dongle, le même câble et la même carte, et lit le flash sans une erreur ;
 *  - un redémarrage de la puce provoqué par la fermeture du port : reproduit en
 *    Python (bascule de débit, close(), open() à 460800), la puce survit,
 *    n'émet AUCUN octet parasite et répond encore — donc ni DTR/RTS ni traîne
 *    dans le tampon ;
 *  - se passer de la bascule en attaquant d'emblée à 460800 : la ROM de l'ESP32
 *    ne parle qu'à 115200 au réveil (« No serial data received » à 460800), et
 *    esptool-js la fixe en dur (`romBaudrate = 115200`).
 *
 * Reste donc un défaut propre à Web Serial dans Chrome, dans la fermeture /
 * réouverture du port que fait `changeBaud()` — que la couche OS, elle, encaisse
 * sans broncher. Faute de pouvoir le corriger chez nous, on ne le subit plus :
 * on garde 460800 en première intention, et si le lien ne tient pas, on
 * recommence à 115200. À 115200, `romBaudrate === baudrate` et esptool-js
 * n'appelle jamais `changeBaud()` : le chemin qui casse n'est plus emprunté.
 *
 * Le repli coûte du temps de flash (de l'ordre d'une minute au lieu de vingt
 * secondes), pas une manipulation : il est automatique et ne demande rien à
 * l'opérateur, qui voit seulement une ligne de journal.
 */

import { ESPLoader, Transport, IEspLoaderTerminal } from "esptool-js";

/** Première intention. Les DevKit à CH340 gardent ce débit. */
export const DEBIT_RAPIDE = 460800;

/** Repli. Débit de la ROM : esptool-js ne tente alors aucune bascule. */
export const DEBIT_SUR = 115200;

// Laisse retomber la traîne émise pendant la bascule avant de vider l'entrée :
// vidée trop tôt, les octets encore en transit arriveraient après le coup de
// balai et le rendraient inutile. Chrome fait transiter le port par un autre
// processus, d'où une latence sans commune mesure avec celle de pyserial.
const REPOS_APRES_BASCULE = 120;
const VIDAGES_APRES_BASCULE = 3;

const attendre = (ms: number) => new Promise((r) => setTimeout(r, ms));

export class ChargeurEsp extends ESPLoader {
  async changeBaud(): Promise<void> {
    await super.changeBaud();
    // Rien de légitime ne peut se trouver dans l'entrée à cet instant : aucune
    // commande n'est en vol entre la bascule et la lecture d'identité du flash
    // qui suit. Tout ce qui traîne est du bruit de cadrage.
    for (let i = 0; i < VIDAGES_APRES_BASCULE; i++) {
      await attendre(REPOS_APRES_BASCULE);
      this.transport.flushInput();
    }
  }
}

/*
 * Rend le port avant une nouvelle tentative : main() le rouvre lui-même, il doit
 * donc être libre. disconnect() boucle dans waitForUnlock(400) TANT QU'un flux
 * reste verrouillé, sans borne — on borne, quitte à laisser le port ouvert.
 */
const rendrePort = async (transport: Transport) => {
  await Promise.race([
    transport.disconnect().catch(() => {}),
    attendre(3000),
  ]);
  await attendre(200);
};

/**
 * Ouvre une session de flash : tente le débit rapide, se rabat sur le débit sûr
 * si le lien ne tient pas. Le `main()` de la seconde tentative refait la
 * séquence DTR/RTS, ce qui ramène la puce en ROM à 115200 quel que soit l'état
 * laissé par la première.
 */
export const ouvrirChargeur = async (
  transport: Transport,
  terminal: IEspLoaderTerminal,
  journaliser: (ligne: string) => void
): Promise<{ loader: ChargeurEsp; puce: string }> => {
  const tenter = async (debit: number) => {
    const loader = new ChargeurEsp({
      transport,
      baudrate: debit,
      terminal,
      debugLogging: false,
    });
    const puce = await loader.main();
    return { loader, puce };
  };

  try {
    return await tenter(DEBIT_RAPIDE);
  } catch (e) {
    const cause = e instanceof Error ? e.message : String(e);
    journaliser(
      `⚠️ Lien instable à ${DEBIT_RAPIDE} bauds (${cause}). Reprise à ${DEBIT_SUR} : plus lent, mais sûr.`
    );
    await rendrePort(transport);
    return await tenter(DEBIT_SUR);
  }
};
